/** Same-origin URL served from public/opencv/opencv.js */
export const OPENCV_SCRIPT_URL = '/opencv/opencv.js';

function isLikelyJavaScriptResponse(response) {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('text/html')) return false;
    return (
        contentType.includes('javascript') ||
        contentType.includes('ecmascript') ||
        contentType.includes('text/plain') ||
        contentType === ''
    );
}

/**
 * Network warm-up only — service worker (precache + CacheFirst on /opencv/) owns storage.
 * A successful fetch lets the SW intercept and cache the response for importScripts offline.
 *
 * @param {{ force?: boolean }} [options] - force: bypass HTTP cache on retry after a bad load
 */
export async function warmOpenCvScriptCache({ force = false } = {}) {
    if (typeof window === 'undefined' || !window.fetch) return;

    try {
        const response = await fetch(OPENCV_SCRIPT_URL, {
            credentials: 'same-origin',
            cache: force ? 'reload' : 'default',
        });
        if (!response.ok) {
            throw new Error(`OpenCV fetch failed: HTTP ${response.status}`);
        }
        if (!isLikelyJavaScriptResponse(response)) {
            const contentType = response.headers.get('content-type') || 'unknown';
            throw new Error(`OpenCV returned invalid content-type: ${contentType}`);
        }
    } catch (err) {
        console.warn('[OpenCV] Warm cache failed:', err);
        /* Offline or blocked; SW precache / CacheFirst may still serve the worker */
    }
}

/**
 * User-facing message when OpenCV worker fails (often missing offline cache on Safari).
 * navigator.onLine is hint-only — not used for control flow.
 */
export function formatOpenCvInitError(err) {
    const detail = err?.message || 'OpenCV failed to load';
    if (detail.includes('Connect to the internet') || detail.includes('open the app once')) {
        return detail;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return (
            `${detail} Image processing needs OpenCV (~11MB) cached on this device. ` +
            'Connect to the internet, open the app until loading finishes, then try offline screening again.'
        );
    }
    return (
        `${detail} Reload the page. For offline use, open the app once on Wi‑Fi so the service worker can cache OpenCV.`
    );
}
